import React, { useState, useMemo, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    Button,
    TextField,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    MenuItem,
    Card,
    CardContent,
    CardActions,
    ToggleButtonGroup,
    ToggleButton,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Divider,
    FormControl,
    InputLabel,
    Select,
    useTheme,
    alpha,
    CircularProgress,
    TableSortLabel,
    Stack
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    FmdGood as GPSIcon,
    ViewModule as CardViewIcon,
    TableChart as TableViewIcon,
    ViewList as CompactViewIcon,
    DirectionsCar as VehicleIcon,
    LocationOff as NoVehicleIcon
} from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useApiCall, useMutation } from '../hooks/useApi';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';

// Safe datetime formatter
const safeFormatDateTime = (value) => {
    try {
        if (!value) return 'N/A';

        let date;
        if (value instanceof Date) {
            date = value;
        } else if (typeof value === 'string' || typeof value === 'number') {
            date = new Date(value);
        } else {
            return 'Invalid Date';
        }

        if (isNaN(date.getTime())) {
            return 'Invalid Date';
        }

        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
        return 'Error';
    }
};

const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Sí' : 'No';
    if (value && typeof value.then === 'function') {
        return fallback;
    }
    if (typeof value === 'object') {
        return fallback;
    }
    return fallback;
};

const getErrorMessage = (error, fallback = 'Error desconocido') => {
    if (!error) return fallback;
    if (error.message && typeof error.message === 'string') {
        return error.message;
    }
    if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
            return detail;
        }
        if (Array.isArray(detail)) {
            return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
        }
    }
    if (error.detail && typeof error.detail === 'string') {
        return error.detail;
    }
    if (error.error && typeof error.error === 'string') {
        return error.error;
    }
    if (typeof error === 'string') {
        return error;
    }
    return fallback;
};

const SimpleLoading = ({ message = 'Cargando...' }) => (
    <Container maxWidth={false} disableGutters>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <CircularProgress />
            <Typography variant="h6" color="text.secondary">
                {message}
            </Typography>
        </Box>
    </Container>
);

const flattenVehicleTree = (vehicleList = [], depth = 0) => {
    if (!Array.isArray(vehicleList)) return [];

    return vehicleList.flatMap((vehicle) => {
        const currentVehicle = {
            ...vehicle,
            nestingDepth: depth,
        };

        return [
            currentVehicle,
            ...flattenVehicleTree(vehicle.child_vehicles || [], depth + 1),
        ];
    });
};

const GPSCardView = ({ gpsDevices, vehicles, onEdit, onDelete, hasPermission, t }) => {
    const getVehicleName = (vehicleId, backendVehicleName = null) => {
        if (!vehicleId) return t('gps.card.noAssigned', 'No asignado');

        const vehicle = vehicles?.find(v => `${v.id}` === `${vehicleId}`);
        if (!vehicle) return ensureString(backendVehicleName, t('gps.card.vehicleNotFound', 'Vehículo no encontrado'));

        return ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${`${vehicleId}`.substring(0, 8)}`);
    };

    return (
        <Grid container spacing={3}>
            {gpsDevices.map((gps) => (
                <Grid item xs={12} sm={6} md={4} key={gps.id}>
                    <Card
                        sx={{
                            height: '100%',
                            transition: 'all 0.2s',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: 4
                            }
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                                    <GPSIcon />
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="h6" noWrap fontWeight={600} fontFamily="monospace">
                                        {ensureString(gps.device_id)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                        {ensureString(gps.model || 'N/A')}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight={600} gutterBottom>
                                    {t('gps.card.vehicleAssignment', 'Asignación de Vehículo')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {gps.vehicle_id ? (
                                        <Chip
                                            icon={<VehicleIcon />}
                                            label={getVehicleName(gps.vehicle_id, gps.vehicle_name)}
                                            size="small"
                                            color="primary"
                                            variant="filled"
                                        />
                                    ) : (
                                        <Chip
                                            icon={<NoVehicleIcon />}
                                            label={t('gps.card.noAssigned', 'No asignado')}
                                            size="small"
                                            color="default"
                                            variant="outlined"
                                        />
                                    )}
                                </Box>
                            </Box>

                            <Typography variant="caption" color="text.secondary">
                                {t('gps.card.created', 'Creado')}: {safeFormatDateTime(gps.created_at)}
                            </Typography>
                        </CardContent>
                        <CardActions>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {onEdit && (
                                    <IconButton size="small" color="primary" onClick={() => onEdit(gps)}>
                                        <EditIcon />
                                    </IconButton>
                                )}
                                {onDelete && (
                                    <IconButton size="small" color="error" onClick={() => onDelete(gps)}>
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </CardActions>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );
};

const CompactListView = ({ gpsDevices, vehicles, onEdit, onDelete, hasPermission, t }) => {
    const getVehicleName = (vehicleId, backendVehicleName = null) => {
        if (!vehicleId) return t('gps.card.noAssigned', 'No asignado');

        const vehicle = vehicles?.find(v => `${v.id}` === `${vehicleId}`);
        if (!vehicle) return ensureString(backendVehicleName, t('gps.card.vehicleNotFound', 'Vehículo no encontrado'));

        return ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${`${vehicleId}`.substring(0, 8)}`);
    };

    return (
        <Paper>
            <List>
                {gpsDevices.map((gps, index) => (
                    <React.Fragment key={gps.id}>
                        <ListItem
                            secondaryAction={
                                hasPermission('manager') && (
                                    <Box>
                                        {onEdit && (
                                            <IconButton size="small" color="primary" onClick={() => onEdit(gps)}>
                                                <EditIcon />
                                            </IconButton>
                                        )}
                                        {onDelete && (
                                            <IconButton size="small" color="error" onClick={() => onDelete(gps)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                )
                            }
                        >
                            <ListItemIcon>
                                <Avatar sx={{ bgcolor: 'info.main' }}>
                                    <GPSIcon />
                                </Avatar>
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="body1" fontWeight={600}>
                                            {ensureString(gps.device_id)}
                                        </Typography>
                                        {gps.vehicle_id ? (
                                            <Chip
                                                icon={<VehicleIcon />}
                                                label={t('gps.card.assigned', 'Asignado')}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                            />
                                        ) : (
                                            <Chip
                                                icon={<NoVehicleIcon />}
                                                label={t('gps.stats.unassigned', 'Sin asignar')}
                                                size="small"
                                                color="default"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                }
                                secondary={`${ensureString(gps.model || 'N/A')} • ${getVehicleName(gps.vehicle_id, gps.vehicle_name)}`}
                            />
                        </ListItem>
                        {index < gpsDevices.length - 1 && <Divider />}
                    </React.Fragment>
                ))}
            </List>
        </Paper>
    );
};

const GPSPage = () => {
    const theme = useTheme();
    const { hasPermission } = useAuth();
    const { showSuccess, showError } = useNotification();
    const { t: originalT, ready: i18nReady } = useTranslation();

    const t = useCallback((key, fallback = null) => {
        try {
            if (!i18nReady) {
                return fallback || key.split('.').pop() || key;
            }
            const result = originalT(key);
            if (result && typeof result === 'object' && typeof result.then === 'function') {
                return fallback || key.split('.').pop() || key;
            }
            if (typeof result === 'string') {
                return result;
            }
            return fallback || key.split('.').pop() || key;
        } catch (error) {
            return fallback || key.split('.').pop() || key;
        }
    }, [i18nReady, originalT]);

    const [viewMode, setViewMode] = useState('table');
    const [searchGPS, setSearchGPS] = useState('');
    const [gpsDialog, setGpsDialog] = useState({ open: false, gps: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, gps: null });
    const [orderBy, setOrderBy] = useState('device_id');
    const [order, setOrder] = useState('asc');
    const [gpsEntries, setGpsEntries] = useState([{ device_id: '', model: '' }]);
    const [gpsVehicleId, setGpsVehicleId] = useState('');
    const [gpsBatchError, setGpsBatchError] = useState(null);
    const [creatingGpsDevices, setCreatingGpsDevices] = useState(false);

    const {
        data: gpsDevices,
        loading: gpsLoading,
        refetch: refetchGPS
    } = useApiCall(api.gps?.getAll || (() => Promise.resolve([])));

    const {
        data: vehicles,
        loading: vehiclesLoading
    } = useApiCall(api.vehicles.getAll);

    const gpsSchema = useMemo(() => {
        return Yup.object({
            device_id: Yup.string()
                .required(t('gps.form.deviceIdRequired', 'ID del dispositivo requerido'))
                .min(3, t('gps.form.deviceIdMinLength', 'Mínimo 3 caracteres')),
            model: Yup.string().optional(),
            vehicle_id: Yup.string().nullable(),
        });
    }, [t]);

    const { mutate: updateGPS, loading: updating } = useMutation(
        ({ id, data }) => api.gps?.update(id, data) || Promise.reject(),
        {
            onSuccess: () => {
                showSuccess(t('gps.actions.gpsUpdated', 'Dispositivo GPS actualizado'));
                setGpsDialog({ open: false, gps: null });
                refetchGPS();
            },
            onError: (error) => {
                const errorMessage = getErrorMessage(error, t('hardcoded.pages_gpspage.error_al_actualizar_dispositivo_gps'));
                showError(errorMessage);
            }
        }
    );

    const { mutate: deleteGPS, loading: deleting } = useMutation(api.gps?.delete || (() => Promise.reject()), {
        onSuccess: () => {
            showSuccess(t('gps.actions.gpsDeleted', 'Dispositivo GPS eliminado'));
            setDeleteDialog({ open: false, gps: null });
            refetchGPS();
        },
        onError: (error) => {
            const errorMessage = getErrorMessage(error, t('hardcoded.pages_gpspage.error_al_eliminar_dispositivo_gps'));
            showError(errorMessage);
        }
    });

    const gpsFormik = useFormik({
        initialValues: {
            device_id: '',
            model: '',
            vehicle_id: '',
        },
        validationSchema: gpsSchema,
        enableReinitialize: true,
        onSubmit: (values) => {
            const submitData = {
                device_id: values.device_id,
                model: values.model || null,
                vehicle_id: values.vehicle_id || null
            };

            updateGPS({ id: gpsDialog.gps.id, data: submitData });
        },
    });

    const handleGpsEntryChange = (index, field, value) => {
        setGpsBatchError(null);
        setGpsEntries((previous) => previous.map((entry, entryIndex) => (
            entryIndex === index
                ? { ...entry, [field]: value }
                : entry
        )));
    };

    const handleAddGpsEntry = () => {
        setGpsBatchError(null);
        setGpsEntries((previous) => [...previous, { device_id: '', model: '' }]);
    };

    const handleRemoveGpsEntry = (index) => {
        setGpsBatchError(null);
        setGpsEntries((previous) => {
            if (previous.length === 1) {
                return previous;
            }
            return previous.filter((_, entryIndex) => entryIndex !== index);
        });
    };

    const resetGpsCreateState = () => {
        setGpsEntries([{ device_id: '', model: '' }]);
        setGpsVehicleId('');
        setGpsBatchError(null);
    };

    const closeGpsDialog = () => {
        setGpsDialog({ open: false, gps: null });
        gpsFormik.resetForm();
        resetGpsCreateState();
    };

    const handleCreateGpsSubmit = async (event) => {
        event.preventDefault();
        setGpsBatchError(null);

        const gpsToCreate = gpsEntries
            .map((entry) => ({
                device_id: ensureString(entry.device_id).trim(),
                model: ensureString(entry.model).trim(),
            }))
            .filter((entry) => entry.device_id);

        if (!gpsToCreate.length) {
            setGpsBatchError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.agrega_al_menos_un_gps'));
            return;
        }

        const duplicateDeviceIds = gpsToCreate.map((entry) => entry.device_id.toLowerCase());
        if (new Set(duplicateDeviceIds).size !== duplicateDeviceIds.length) {
            setGpsBatchError(t('hardcoded.components_vehicles_detail_vehicleconnectivitytab.no_puedes_registrar_ids_repetidos'));
            return;
        }

        setCreatingGpsDevices(true);
        try {
            for (const entry of gpsToCreate) {
                await api.gps.create({
                    device_id: entry.device_id,
                    model: entry.model || null,
                    vehicle_id: gpsVehicleId || null,
                });
            }

            showSuccess(
                gpsToCreate.length === 1
                    ? t('gps.actions.gpsCreated', 'Dispositivo GPS creado exitosamente')
                    : `t("hardcoded.pages_gpspage.gps_creados", { count: gpsToCreate.length })`
            );
            closeGpsDialog();
            await refetchGPS();
        } catch (error) {
            const errorMessage = getErrorMessage(error, t('hardcoded.pages_gpspage.error_al_crear_dispositivos_gps'));
            setGpsBatchError(errorMessage);
            showError(errorMessage);
        } finally {
            setCreatingGpsDevices(false);
        }
    };

    const handleGpsDialog = (gps = null) => {
        setGpsDialog({ open: true, gps });
        if (gps) {
            gpsFormik.setValues({
                device_id: gps.device_id,
                model: gps.model || '',
                vehicle_id: gps.vehicle_id || '',
            });
        } else {
            gpsFormik.resetForm();
            resetGpsCreateState();
        }
    };

    const handleDelete = (gps) => {
        setDeleteDialog({ open: true, gps });
    };

    const confirmDelete = () => {
        if (deleteDialog.gps) {
            deleteGPS(deleteDialog.gps.id);
        }
    };

    const flatVehicles = useMemo(() => flattenVehicleTree(vehicles), [vehicles]);

    const getVehicleName = useCallback((vehicleId, backendVehicleName = null) => {
        if (!vehicleId) return t('gps.card.noAssigned', 'No asignado');

        const vehicle = flatVehicles?.find(v => `${v.id}` === `${vehicleId}`);
        if (!vehicle) return ensureString(backendVehicleName, t('gps.card.vehicleNotFound', 'Vehículo no encontrado'));

        const displayName = vehicle.display_name || vehicle.alias || `Vehículo ${`${vehicleId}`.substring(0, 8)}`;
        return ensureString(displayName);
    }, [flatVehicles, t]);

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const descendingComparator = useCallback((a, b, currentOrderBy) => {
        let aValue;
        let bValue;

        if (currentOrderBy === 'vehicle') {
            aValue = getVehicleName(a.vehicle_id, a.vehicle_name);
            bValue = getVehicleName(b.vehicle_id, b.vehicle_name);
        } else if (currentOrderBy === 'status') {
            aValue = a.vehicle_id ? 'assigned' : 'unassigned';
            bValue = b.vehicle_id ? 'assigned' : 'unassigned';
        } else if (currentOrderBy === 'created_at') {
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
        } else {
            aValue = a[currentOrderBy] || '';
            bValue = b[currentOrderBy] || '';
        }

        if (bValue < aValue) {
            return -1;
        }
        if (bValue > aValue) {
            return 1;
        }
        return 0;
    }, [getVehicleName]);

    const getComparator = useCallback((currentOrder, currentOrderBy) => {
        return currentOrder === 'desc'
            ? (a, b) => descendingComparator(a, b, currentOrderBy)
            : (a, b) => -descendingComparator(a, b, currentOrderBy);
    }, [descendingComparator]);

    const filteredAndSortedGPS = React.useMemo(() => {
        const filtered = gpsDevices?.filter(gps =>
            gps.device_id.toLowerCase().includes(searchGPS.toLowerCase()) ||
            (gps.model && gps.model.toLowerCase().includes(searchGPS.toLowerCase())) ||
            (gps.vehicle_name && gps.vehicle_name.toLowerCase().includes(searchGPS.toLowerCase()))
        ) || [];

        return filtered.sort(getComparator(order, orderBy));
    }, [gpsDevices, searchGPS, order, orderBy, getComparator]);

    const viewModeOptions = [
        { value: 'table', label: t('hardcoded.pages_gpspage.view_table'), icon: <TableViewIcon /> },
        { value: 'cards', label: t('hardcoded.pages_gpspage.view_cards'), icon: <CardViewIcon /> },
        { value: 'compact', label: t('hardcoded.pages_gpspage.view_compact'), icon: <CompactViewIcon /> }
    ];

    if (gpsLoading || vehiclesLoading) {
        return <SimpleLoading message={t('gps.loadingMessage', 'Cargando dispositivos GPS...')} />;
    }

    const renderGPSContent = () => {
        if (filteredAndSortedGPS.length === 0) {
            return (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <GPSIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {t('gps.noGPSFound', 'No se encontraron dispositivos GPS')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {searchGPS ?
                            t('gps.adjustCriteria', 'Intenta ajustar los criterios de búsqueda') :
                            t('gps.registerFirst', 'Registra tu primer dispositivo GPS')}
                    </Typography>
                </Paper>
            );
        }

        switch (viewMode) {
            case 'cards':
                return (
                    <GPSCardView
                        gpsDevices={filteredAndSortedGPS}
                        vehicles={flatVehicles}
                        onEdit={hasPermission('manager') ? handleGpsDialog : undefined}
                        onDelete={hasPermission('manager') ? handleDelete : undefined}
                        hasPermission={hasPermission}
                        t={t}
                    />
                );
            case 'compact':
                return (
                    <CompactListView
                        gpsDevices={filteredAndSortedGPS}
                        vehicles={flatVehicles}
                        onEdit={hasPermission('manager') ? handleGpsDialog : undefined}
                        onDelete={hasPermission('manager') ? handleDelete : undefined}
                        hasPermission={hasPermission}
                        t={t}
                    />
                );
            default:
                return (
                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'device_id'}
                                                direction={orderBy === 'device_id' ? order : 'asc'}
                                                onClick={() => handleRequestSort('device_id')}
                                            >
                                                {t('gps.table.deviceId', 'ID del Dispositivo')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'model'}
                                                direction={orderBy === 'model' ? order : 'asc'}
                                                onClick={() => handleRequestSort('model')}
                                            >
                                                {t('gps.table.model', 'Modelo')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'vehicle'}
                                                direction={orderBy === 'vehicle' ? order : 'asc'}
                                                onClick={() => handleRequestSort('vehicle')}
                                            >
                                                {t('gps.table.assignedVehicle', 'Vehículo Asignado')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'status'}
                                                direction={orderBy === 'status' ? order : 'asc'}
                                                onClick={() => handleRequestSort('status')}
                                            >
                                                {t('gps.table.status', 'Estado')}
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell>
                                            <TableSortLabel
                                                active={orderBy === 'created_at'}
                                                direction={orderBy === 'created_at' ? order : 'asc'}
                                                onClick={() => handleRequestSort('created_at')}
                                            >
                                                {t('gps.table.creationDate', 'Fecha de Creación')}
                                            </TableSortLabel>
                                        </TableCell>
                                        {hasPermission('manager') && (
                                            <TableCell align="center">{t('gps.table.actions', 'Acciones')}</TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredAndSortedGPS.map((gps) => (
                                        <TableRow key={gps.id} hover>
                                            <TableCell>
                                                <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                                                    {ensureString(gps.device_id)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {ensureString(gps.model || 'N/A')}
                                            </TableCell>
                                            <TableCell>
                                                {getVehicleName(gps.vehicle_id, gps.vehicle_name)}
                                            </TableCell>
                                            <TableCell>
                                                {gps.vehicle_id ? (
                                                    <Chip
                                                        label={t('gps.card.assigned', 'Asignado')}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        label={t('gps.stats.unassigned', 'Sin asignar')}
                                                        size="small"
                                                        color="default"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {safeFormatDateTime(gps.created_at)}
                                            </TableCell>
                                            {hasPermission('manager') && (
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                        <Tooltip title={t('gps.actions.editGPS', 'Editar dispositivo GPS')}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleGpsDialog(gps)}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={t('gps.actions.deleteGPS', 'Eliminar dispositivo GPS')}>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => handleDelete(gps)}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                );
        }
    };

    return (
        <Container maxWidth={false} disableGutters>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight={700} gutterBottom>
                    {t('gps.title', 'Gestión de Dispositivos GPS')}
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={t('gps.search', 'Buscar dispositivos GPS...')}
                        value={searchGPS}
                        onChange={(e) => setSearchGPS(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            ),
                        }}
                        size="small"
                        sx={{ width: 300 }}
                    />

                    <Tooltip title={t('common.refresh', 'Actualizar')}>
                        <IconButton
                            onClick={refetchGPS}
                            size="small"
                            color="primary"
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                }
                            }}
                        >
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>

                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newMode) => newMode && setViewMode(newMode)}
                        size="small"
                    >
                        {viewModeOptions.map((option) => (
                            <ToggleButton key={option.value} value={option.value}>
                                <Tooltip title={option.label}>
                                    {option.icon}
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                {hasPermission('manager') && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleGpsDialog()}
                        size="large"
                    >
                        {t('gps.registerGPS', 'Registrar Dispositivo GPS')}
                    </Button>
                )}
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="info.main">
                                {gpsDevices?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('gps.stats.totalGPS', 'Total de Dispositivos GPS')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="success.main">
                                {gpsDevices?.filter(g => g.vehicle_id).length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('gps.stats.assigned', 'Asignados')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="warning.main">
                                {gpsDevices?.filter(g => !g.vehicle_id).length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('gps.stats.unassigned', 'Sin Asignar')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 2 }}>
                            <Typography variant="h4" fontWeight={700} color="info.main">
                                {vehicles?.length || 0}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('gps.stats.availableVehicles', 'Vehículos Disponibles')}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {renderGPSContent()}

            {/* GPS Dialog */}
            <Dialog
                open={gpsDialog.open}
                onClose={closeGpsDialog}
                maxWidth="sm"
                fullWidth
            >
                <form onSubmit={gpsDialog.gps ? gpsFormik.handleSubmit : handleCreateGpsSubmit}>
                    <DialogTitle>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GPSIcon color="primary" />
                            {gpsDialog.gps ?
                                t('gps.form.editTitle', 'Editar Dispositivo GPS') :
                                t('gps.form.createTitle', 'Registrar Nuevo Dispositivo GPS')}
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ pt: 2 }}>
                            {gpsDialog.gps ? (
                                <>
                                    <TextField
                                        fullWidth
                                        label={t('gps.form.deviceId', 'ID del Dispositivo')}
                                        name="device_id"
                                        value={gpsFormik.values.device_id}
                                        onChange={gpsFormik.handleChange}
                                        onBlur={gpsFormik.handleBlur}
                                        error={gpsFormik.touched.device_id && Boolean(gpsFormik.errors.device_id)}
                                        helperText={gpsFormik.touched.device_id && gpsFormik.errors.device_id}
                                        placeholder="GPS-12345"
                                        sx={{ mb: 3 }}
                                    />

                                    <TextField
                                        fullWidth
                                        label={t('gps.form.model', 'Modelo')}
                                        name="model"
                                        value={gpsFormik.values.model}
                                        onChange={gpsFormik.handleChange}
                                        onBlur={gpsFormik.handleBlur}
                                        error={gpsFormik.touched.model && Boolean(gpsFormik.errors.model)}
                                        helperText={gpsFormik.touched.model && gpsFormik.errors.model}
                                        placeholder="Ej: TK103, GT06, etc"
                                        sx={{ mb: 3 }}
                                    />

                                    <FormControl fullWidth>
                                        <InputLabel>{t('gps.form.assignedVehicle', 'Vehículo Asignado')}</InputLabel>
                                        <Select
                                            name="vehicle_id"
                                            value={gpsFormik.values.vehicle_id}
                                            onChange={gpsFormik.handleChange}
                                            label={t('gps.form.assignedVehicle', 'Vehículo Asignado')}
                                        >
                                            <MenuItem value="">
                                                <em>{t('gps.form.noAssignment', 'Sin asignación')}</em>
                                            </MenuItem>
                                            {flatVehicles?.map((vehicle) => (
                                                <MenuItem key={vehicle.id} value={vehicle.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <VehicleIcon fontSize="small" />
                                                        <Typography>
                                                            {`${'— '.repeat(vehicle.nestingDepth || 0)}${ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicle.id.substring(0, 8)}`)}`}
                                                        </Typography>
                                                        {vehicle.parent_vehicle_id && (
                                                            <Chip label={t('gps.form.trailer', 'Remolque')} size="small" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </>
                            ) : (
                                <Stack spacing={3}>
                                    {gpsBatchError && (
                                        <Alert severity="error">{gpsBatchError}</Alert>
                                    )}

                                    <Typography variant="body2" color="text.secondary">
                                        {t('hardcoded.pages_gpspage.registra_varios_gps_y_asignalos')}</Typography>

                                    <FormControl fullWidth>
                                        <InputLabel>{t('gps.form.assignedVehicle', 'Vehículo Asignado')}</InputLabel>
                                        <Select
                                            value={gpsVehicleId}
                                            onChange={(event) => {
                                                setGpsBatchError(null);
                                                setGpsVehicleId(event.target.value);
                                            }}
                                            label={t('gps.form.assignedVehicle', 'Vehículo Asignado')}
                                        >
                                            <MenuItem value="">
                                                <em>{t('gps.form.noAssignment', 'Sin asignación')}</em>
                                            </MenuItem>
                                            {flatVehicles?.map((vehicle) => (
                                                <MenuItem key={vehicle.id} value={vehicle.id}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                        <VehicleIcon fontSize="small" />
                                                        <Typography>
                                                            {`${'— '.repeat(vehicle.nestingDepth || 0)}${ensureString(vehicle.display_name || vehicle.alias || `Vehículo ${vehicle.id.substring(0, 8)}`)}`}
                                                        </Typography>
                                                        {vehicle.parent_vehicle_id && (
                                                            <Chip label={t('gps.form.trailer', 'Remolque')} size="small" variant="outlined" />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    <Stack spacing={2}>
                                        {gpsEntries.map((entry, index) => (
                                            <Box
                                                key={`gps-entry-${index}`}
                                                sx={{
                                                    p: 2,
                                                    border: 1,
                                                    borderColor: 'divider',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 2,
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <Typography variant="subtitle2" fontWeight={600}>
                                                        GPS {index + 1}
                                                    </Typography>
                                                    {gpsEntries.length > 1 && (
                                                        <IconButton color="error" onClick={() => handleRemoveGpsEntry(index)}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </Box>

                                                <TextField
                                                    fullWidth
                                                    label={t('gps.form.deviceId', 'ID del Dispositivo')}
                                                    value={entry.device_id}
                                                    onChange={(event) => handleGpsEntryChange(index, 'device_id', event.target.value)}
                                                    placeholder="GPS-12345"
                                                />

                                                <TextField
                                                    fullWidth
                                                    label={t('gps.form.model', 'Modelo')}
                                                    value={entry.model}
                                                    onChange={(event) => handleGpsEntryChange(index, 'model', event.target.value)}
                                                    placeholder="Ej: TK103, GT06, etc"
                                                />
                                            </Box>
                                        ))}
                                    </Stack>

                                    <Button type="button" variant="outlined" startIcon={<AddIcon />} onClick={handleAddGpsEntry}>
                                        {t("hardcoded.pages_gpspage.agregar_otro_gps")}
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeGpsDialog}>
                            {t('gps.form.cancel', 'Cancelar')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={creatingGpsDevices || updating}
                        >
                            {gpsDialog.gps ?
                                t('gps.form.update', 'Actualizar') :
                                t('gps.form.register', 'Registrar')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, gps: null })}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DeleteIcon color="error" />
                        {t('gps.actions.confirmDelete', 'Confirmar Eliminación')}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        {t('gps.actions.deleteMessage', '¿Estás seguro de que quieres eliminar este dispositivo GPS? Esta acción no se puede deshacer.')}
                    </Typography>
                    {deleteDialog.gps && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                                {ensureString(deleteDialog.gps.device_id)} - {ensureString(deleteDialog.gps.model || 'N/A')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {getVehicleName(deleteDialog.gps.vehicle_id, deleteDialog.gps.vehicle_name)}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, gps: null })}>
                        {t('common.cancel', 'Cancelar')}
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={confirmDelete}
                        disabled={deleting}
                        startIcon={<DeleteIcon />}
                    >
                        {t('common.delete', 'Eliminar')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default GPSPage;
